import * as Sentry from '@sentry/react';
import { usePostHog } from 'posthog-js/react';

import { useDetailedMerchantListQuery } from '~src/pages/merchant/queries';

export enum TrackingEventEnum {
  // Create regular shipment
  ShipmentCreateStart = 'shipment.create.start',
  ShipmentCreateCompleted = 'shipment.create.completed',
  ShipmentCreateFailed = 'shipment.create.failed',

  // Create duplicate shipment
  ShipmentCreateDuplicateStart = 'shipment.create-duplicate.start',
  ShipmentCreateDuplicateCompleted = 'shipment.create-duplicate.completed',
  ShipmentCreateDuplicateFailed = 'shipment.create-duplicate.failed',

  // Create return shipment
  ShipmentCreateReturnStart = 'shipment.create-return.start',
  ShipmentCreateReturnCompleted = 'shipment.create-return.completed',
  ShipmentCreateReturnFailed = 'shipment.create-return.failed',

  // Create shipment from TOS
  ShipmentCreateFromTosStart = 'shipment.create-from-tos.start',
  ShipmentCreateFromTosCompleted = 'shipment.create-from-tos.completed',
  ShipmentCreateFromTosFailed = 'shipment.create-from-tos.failed',

  // Create shipment from pending
  ShipmentCreateFromPendingStart = 'shipment.create-from-pending.start',
  ShipmentCreateFromPendingCompleted = 'shipment.create-from-pending.completed',
  ShipmentCreateFromPendingFailed = 'shipment.create-from-pending.failed',

  // Edit shipment
  ShipmentEditStart = 'shipment.edit.start',
  ShipmentEditCompleted = 'shipment.edit.completed',
  ShipmentEditCancel = 'shipment.edit.cancel',
  ShipmentEditFailed = 'shipment.edit.failed',

  // Book shipment
  ShipmentBookingCompleted = 'shipment.booking.completed',
  ShipmentBookingFailed = 'shipment.booking.failed',

  // Schedule pickup
  ShipmentSchedulePickupCompleted = 'shipment.schedule-pickup.completed',
  ShipmentSchedulePickupFailed = 'shipment.schedule-pickup.failed',

  // Cancel shipment
  ShipmentCancelCompleted = 'shipment.cancel.completed',
  ShipmentCancelFailed = 'shipment.cancel.failed',

  // Booking rule configurator
  ConfiguratorClickHelp = 'booking-rule-configurator.toolbar.help',
  ConfiguratorClickFormat = 'booking-rule-configurator.toolbar.format',
  ConfiguratorClickTestMode = 'booking-rule-configurator.toolbar.test-mode',
  ConfiguratorPublishCompleted = 'booking-rule-configurator.publish.completed',
  ConfiguratorPublishFailed = 'booking-rule-configurator.publish.failed',
  ConfiguratorRunTestCompleted = 'booking-rule-configurator.test-run.completed',
  ConfiguratorRunTestFailed = 'booking-rule-configurator.test-run.failed',
  ConfiguratorPrefillShipmentOpen = 'booking-rule-configurator.pre-fill-shipment.open',
  ConfiguratorPrefillShipmentClose = 'booking-rule-configurator.pre-fill-shipment.close',
  ConfiguratorPrefillShipmentCompleted = 'booking-rule-configurator.pre-fill-shipment.completed',
  ConfiguratorPrefillShipmentFailed = 'booking-rule-configurator.pre-fill-shipment.failed',
  ConfiguratorClickTab = 'booking-rule-configurator.tab.click',
}

export function useTracking() {
  const { merchants } = useDetailedMerchantListQuery();
  const posthog = usePostHog();

  function identify(id: string, email: string | undefined) {
    const posthogId = posthog.get_distinct_id();

    /**
     * Due to React re-renders we only identify the user given that it's not already identified.
     */
    if (posthogId !== id) {
      posthog.identify(id, { email });
    }

    Sentry.setUser({ id, email });
  }

  function group(siteId: string) {
    const merchant = merchants.find(m => m.sites.some(s => s.id === siteId));
    const site = merchant?.sites.find(s => s.id === siteId);
    const groups = posthog.getGroups();

    if (merchant) {
      /**
       * Due to React re-renders we only group by the merchant given that it's not already grouped.
       */
      if (groups.merchant !== merchant.id) {
        posthog.group('merchant', merchant.id!, {
          name: merchant.name,
        });
      }

      Sentry.setTag('ingrid.merchant.id', merchant.id);
      Sentry.setTag('ingrid.merchant.name', merchant.name);
    }

    if (site) {
      /**
       * Due to React re-renders we only group by the site given that it's not already grouped.
       */
      if (groups.site !== site.id) {
        posthog.group('site', site.id!, {
          name: site.name,
        });
      }

      Sentry.setTag('ingrid.site.id', site.id);
      Sentry.setTag('ingrid.site.name', site.name);
    }
  }

  function capture(event: TrackingEventEnum, properties?: Record<string, unknown>) {
    posthog.capture(event, properties);
  }

  return {
    capture,
    identify,
    group,
  };
}
